import React, { useState } from "react";
import { Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

// Predefined set of distinct colors
const distinctColors = [
  "#FF5733",
  "#33FF57",
  "#3357FF",
  "#F39C12",
  "#9B59B6",
  "#1ABC9C",
  "#E74C3C",
];

const GuideGraph = ({ selectedGuides = [], onDropGuide = () => {} }) => {
  const labels = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const getGuideColor = (index) =>
    distinctColors[index % distinctColors.length];

  const generateChartData = () => {
    const years = new Set(
      selectedGuides.map((g) => g.selectedYear).filter((y) => y && y !== "All")
    );

    let allMonths = [];

    if (years.size === 1) {
      const year = [...years][0];
      for (let m = 0; m < 12; m++) {
        allMonths.push(
          `${new Date(year, m).toLocaleString("en", { month: "short" })} '${year
            .toString()
            .slice(-2)}`
        );
      }
    } else {
      const today = new Date();
      const startYear = 2024;
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();

      for (let y = startYear; y <= currentYear; y++) {
        for (let m = 0; m < 12; m++) {
          const formatted = `${new Date(y, m).toLocaleString("en", {
            month: "short",
          })} '${y.toString().slice(-2)}`;
          allMonths.push(formatted);

          if (y === currentYear && m === currentMonth) break;
        }
      }
    }

    return {
      labels: allMonths,
      datasets: selectedGuides.map((guide, index) => {
        const monthRatingsMap = {};
        guide.monthlyRatings.forEach((entry) => {
          monthRatingsMap[entry.month] = parseFloat(entry.averageRating);
        });

        const data = allMonths.map((month) =>
          monthRatingsMap[month] !== undefined ? monthRatingsMap[month] : null
        );

        return {
          label: `${guide.name} (${guide.selectedYear})`,
          data,
          borderColor: getGuideColor(index),
          backgroundColor: "transparent",
          borderWidth: 1,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.2,
          guideIndex: index, // ✅ added index reference
        };
      }),
    };
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const rawData = event.dataTransfer.getData("application/json");
    if (!rawData) return;

    const guideData = JSON.parse(rawData);
    if (
      !selectedGuides.some(
        (g) =>
          g.name === guideData.name && g.selectedYear === guideData.selectedYear
      )
    ) {
      onDropGuide({ ...guideData });
    }
  };

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      style={{
        border: "2px dashed #ccc",
        padding: "10px",
        height: "60vh",
        textAlign: "center",
        backgroundColor: "#f9f9f9",
        borderRadius: "10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <h3>Guide Performance Over Months</h3>
      {selectedGuides.length > 0 ? (
        <div style={{ width: "100%", height: "60vh", position: "relative" }}>
          <Line
            data={generateChartData()}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              animation: { duration: 0 },
              plugins: {
                legend: {
                  position: "top",
                  labels: {
                    usePointStyle: true,
                    pointStyle: "circle",
                    font: { size: 10 },
                    generateLabels: (chart) => {
                      return chart.data.datasets.map((dataset, index) => ({
                        text: dataset.label,
                        fillStyle: dataset.borderColor,
                        strokeStyle: dataset.borderColor,
                        lineWidth: 0,
                        pointStyle: "circle",
                        hidden: !chart.isDatasetVisible(index),
                        datasetIndex: index,
                      }));
                    },
                  },
                  onClick: (e, legendItem, legend) => {
                    const index = legendItem.datasetIndex;
                    const chart = legend.chart;
                    const meta = chart.getDatasetMeta(index);

                    if (meta) {
                      meta.hidden = meta.hidden === null ? true : !meta.hidden;
                      chart.update();
                    }
                  },
                },
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      const dataset = context.dataset;
                      const guide = selectedGuides[dataset.guideIndex]; // ✅ direct reference
                      const rating = context.raw;
                      const month = context.label;

                      if (!guide) return `${dataset.label} - ${rating} - No reviews`;

                      const monthData = guide.monthlyRatings.find(
                        (m) => m.month === month
                      );
                      const reviewCount = monthData ? monthData.reviewCount : 0;

                      if (rating === null) {
                        return `${dataset.label} - No data available`;
                      }
                      return `${dataset.label} - ${rating} - ${reviewCount} review${
                        reviewCount !== 1 ? "s" : ""
                      }`;
                    },
                  },
                },
              },
              scales: {
                x: {
                  title: { display: true, text: "Month" },
                  ticks: { padding: 0, font: { size: 10 } },
                },
                y: {
                  title: { display: true, text: "Average Rating" },
                  min: 3,
                  max: 5,
                  ticks: { stepSize: 0.1 },
                  afterDataLimits: (scale) => {
                    scale.max += 0.1;
                  },
                },
              },
            }}
          />
        </div>
      ) : (
        <p style={{ color: "#666", fontSize: "16px" }}>
          Drag and drop guides here(From Guide List above) to compare
          performance.
        </p>
      )}
    </div>
  );
};

export default GuideGraph;
